import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
const { notifSend } = notifActions;
import { IManagePoll, IManagePollAction } from 'models/polls/managepoll';
import { IPoll } from 'models/polls/poll';
import { updatePollInList, addPollInList } from 'redux/modules/polls/polls';
import { PollStatus } from 'models/enums/statuses';
import { IPollOption } from 'models/polls/polloptions';
import { cookies } from 'helpers/cookies';
import { closeModal } from 'redux/modules/modal';

/** Action Types */
export const REQUEST_START: string = 'managepoll/REQUEST_START';
export const REQUEST_FAILURE: string = 'managepoll/REQUEST_FAILURE';
export const GET_POLL_SUCCESS: string = 'managepoll/GET_POLL_SUCCESS';
export const UPDATE_POLL_SUCCESS: string = 'managepoll/UPDATE_POLL_SUCCESS';
export const SET_IS_EDIT_MODE: string = 'managepoll/SET_IS_EDIT_MODE';
export const RESET_DATA: string = 'managepoll/RESET_DATA';
export const SET_SELECTED_POLL_ID: string = 'managepoll/SET_SELECTED_POLL_ID';
export const CLEAR_FORM_DATA: string = 'managepoll/CLEAR_FORM_DATA';
export const VALIDATION_ERROR: string = 'managepoll/VALIDATION_ERROR';
export const CLEAR_FORM_ERROR: string = 'managepoll/CLEAR_FORM_ERROR';
export const VOTE_SUCCESS: string = 'managepoll/VOTE_SUCCESS';

/** Initial State */
const initialState: IManagePoll = {
	isFetching: false,
	error: null,
	poll: {
		questionText: '',
		votes: 0,
		userName: '',
		status: PollStatus.Draft,
	},
	isEditMode: false,
	selectedPollId: null,
};

/** Reducer */
export function managePollReducer(state = initialState, action: IManagePollAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case GET_POLL_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				poll: action.payload.poll,
			});

		case UPDATE_POLL_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				poll: action.payload.poll,
			});

		case SET_IS_EDIT_MODE:
			return Object.assign({}, state, {
				isEditMode: action.payload.isEditMode,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				poll: {
					questionText: '',
					votes: 0,
					userName: '',
					status: PollStatus.Draft,
				},
			});

		case VALIDATION_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case CLEAR_FORM_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: null,
				error: false,
			});

		case SET_SELECTED_POLL_ID:
			return Object.assign({}, state, {
				selectedPollId: action.payload.selectedPollId,
			});

		case VOTE_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		default:
			return state;
	}
}

/** Action Creator - create */
export function submitCreatePoll(poll: IPoll, approve?: boolean) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(poll),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newPoll: IPoll) => {
						dispatch(getPollSuccess(newPoll));
						dispatch(addPollInList(newPoll));
						dispatch(closeModal());

						if (approve === true) {
							dispatch(setPollStatus(newPoll.id, PollStatus.InApproval));
						}
						dispatch(
							notifSend({ message: i18n.t('Notification_Success_Poll_Created'), kind: 'success', dismissAfter: 3000 }),
						);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
							dispatch(closeModal());
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function submitUpdatePoll(poll: IPoll, approve?: boolean) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll/' + poll.id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(poll),
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updatePollSuccess(poll));
					dispatch(updatePollInList(poll));
					if (approve === true) {
						dispatch(setPollStatus(poll.id, PollStatus.InApproval));
					}
					dispatch(
						notifSend({ message: i18n.t('Notification_Success_Poll_Updated'), kind: 'success', dismissAfter: 3000 }),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
							dispatch(closeModal());
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getPoll(id: string) {
	return (dispatch, getState: () => IStore) => {
		if (id) {
			dispatch(setIsEditMode(true));
			dispatch(requestStart());

			return fetch(
				getState().fetchDefault.baseAPI + 'api/Poll/' + id + '/Edit',
				Object.assign({}, getState().fetchDefault.requestInit),
			)
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: IPoll) => {
							dispatch(getPollSuccess(res));
						});
					} else {
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								dispatch(requestFailure(res));
							})
							.catch(() => {
								const message: any = { status, statusText };
								dispatch(requestFailure(message));
							});
					}
				})
				.catch((err) => {
					dispatch(requestFailure(err));
				});
		} else {
			dispatch(clearFormData());
		}
	};
}

/** Action Creator */
export function setPollStatus(id: string, status: PollStatus) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll/' + id + '/SetStatus/' + status,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updatePollStatus(id, status));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updatePollStatus(id: string, status: PollStatus) {
	return (dispatch, getState: () => IStore) => {
		if (id === getState().managePoll.selectedPollId) {
			const poll = getState().managePoll.poll;
			poll.status = status;
			dispatch(getPollSuccess(poll));
		}

		if (getState().polls) {
			const items = getState().polls.items;
			const itemInList = items.filter((x) => x.id === id)[0];
			if (itemInList) {
				itemInList.status = status;

				dispatch(updatePollInList(itemInList));
			}
		}
		if (status === PollStatus.Archived) {
			dispatch(
				notifSend({ message: i18n.t('Notification_Success_Poll_Archived'), kind: 'success', dismissAfter: 3000 }),
			);
		}
	};
}

/** Action Creator - create */
export function submitVotePoll(pollId: string, pollOption: IPollOption) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll/Vote/' + pollOption.id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(pollOption),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((success: number) => {
						switch (success) {
							case 0:
								alert('Error');
								break;

							case 1:
								const expiryDate = new Date();
								expiryDate.setMonth(expiryDate.getFullYear() + 1);
								cookies.set('POLL_' + pollId, 'POLL_' + pollId, { path: '/', expires: expiryDate });
								break;

							default:
								break;
						}

						dispatch(voteSuccess());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IManagePollAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManagePollAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getPollSuccess(poll: IPoll): IManagePollAction {
	return {
		type: GET_POLL_SUCCESS,
		payload: {
			poll,
		},
	};
}

/** Action Creator */
export function updatePollSuccess(poll: IPoll): IManagePollAction {
	return {
		type: UPDATE_POLL_SUCCESS,
		payload: {
			poll,
		},
	};
}

/** Action Creator */
export function setIsEditMode(isEditMode: boolean): IManagePollAction {
	return {
		type: SET_IS_EDIT_MODE,
		payload: {
			isEditMode,
		},
	};
}

/** Action Creator */
export function clearFormData(): IManagePollAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function clearManagePollFormError(): IManagePollAction {
	return {
		type: CLEAR_FORM_ERROR,
	};
}

/** Action Creator */
export function setManagePollValidationFailure(error: any): IManagePollAction {
	return {
		type: VALIDATION_ERROR,
		payload: {
			message: error,
		},
	};
}

/** Action Creator */
export function setSelectedPollId(selectedPollId: string): IManagePollAction {
	return {
		type: SET_SELECTED_POLL_ID,
		payload: {
			selectedPollId,
		},
	};
}

/** Action Creator */
export function voteSuccess(): IManagePollAction {
	return {
		type: VOTE_SUCCESS,
	};
}
